import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Boxes, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!loading && user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="app-bg min-h-dvh grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 text-white" style={{ background: 'linear-gradient(160deg, #123A63 0%, #0E7490 100%)' }}>
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15"><Boxes className="h-6 w-6" /></div>
          <div><div className="font-heading text-xl font-semibold">Lailany</div><div className="text-sm text-white/70">Inventory Manager</div></div>
        </div>
        <div>
          <h2 className="font-heading text-4xl font-semibold leading-tight">Run your warehouse<br/>with confidence.</h2>
          <p className="mt-4 text-white/80 max-w-md">Track inventory, scan barcodes, manage purchases, employee requests, invoices, commissions and profit — all in one place.</p>
        </div>
        <div className="text-sm text-white/60">Lailany Inc · 351 N Walnut Rd Ste 3, Turlock, CA 95382 · 209-559-4600</div>
      </div>

      <div className="flex items-center justify-center p-6 pt-safe pb-safe px-safe">
        <Card className="w-full max-w-md p-8 shadow-lg">
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--primary))] text-white"><Boxes className="h-5 w-5" /></div>
            <div className="font-heading text-lg font-semibold">Lailany Inventory</div>
          </div>
          <h1 className="font-heading text-2xl font-semibold">Sign in</h1>
          <p className="text-sm text-muted-foreground mt-1">Enter your credentials to continue.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@lailany.com" required data-testid="login-email-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="login-password-input" className="mt-1.5" />
            </div>
            <Button type="submit" className="w-full" disabled={submitting} data-testid="login-form-submit-button">
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Sign in
            </Button>
          </form>
          <div className="mt-6 rounded-lg bg-[hsl(var(--secondary))] p-3 text-xs text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Demo accounts</p>
            <p>Admin: admin@lailany.com / Admin@123</p>
            <p>Employee: employee@lailany.com / Employee@123</p>
          </div>
        </Card>
      </div>
    </div>
  );
}
